import React, { useCallback, useEffect } from 'react';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import {
    euclideanDistanceMm,
    PointMm,
} from '~/domain/geometry/patientGeometry';
import {
    LineProfile,
    LineProfileDraft,
    lineProfileInFrame,
    ProfileEndpoint,
    withMovedEndpoint,
} from '~/domain/intensity/lineProfile';
import { paneImageOf } from '~/engine/view/paneImage';
import {
    canvasMidpoint,
    isPointInsideDisplayedImage,
    patientToCanvasPoint,
    viewPlaneCoordinateMm,
} from '~/engine/view/planeViewGeometry';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { useAnnotationPointDrag } from '~/hooks/viewer/annotationOverlay';
import { updateAnnotation } from '~/state/atoms/annotationListAtoms';
import {
    draggingLineProfileState,
    highlightedLineProfileState,
    intensityPlacementActiveState,
    lineProfileDraftState,
    lineProfileListState,
} from '~/state/atoms/intensityToolAtoms';
import { formatDistanceCm } from '~/utils/measurementFormat';
import { OFF_PLANE_OPACITY } from '../AnnotationOverlaySvg';
import AnnotationPlaneOverlay, {
    AnnotationAppearance,
    OVERLAY_LABEL_STYLE,
} from '../AnnotationPlaneOverlay';

/** Committed line-profile colour (blue). */
export const PROFILE_COLOR = '#42A5F5';
/** In-progress placement colour. */
const DRAFT_COLOR = '#90CAF9';
const HANDLE_PX = 4;

interface LineProfileOverlayProps {
    view: PlaneView | null;
}

interface EndpointDrag {
    readonly id: string;
    readonly endpoint: ProfileEndpoint;
    /**
     * The endpoint that stays put. The dragged point is kept on the plane
     * through it, so a profile cannot come out of an edit spanning two slices.
     */
    readonly anchorMm: PointMm;
}

/** Both endpoints must lie on the shown slice for a profile to be on it. */
function profilePoints(profile: LineProfile): readonly PointMm[] {
    return [profile.startMm, profile.endMm];
}

/**
 * Endpoint drag. The moved point is pinned to the plane through the profile's
 * other endpoint, measured along the view's current normal: that keeps the
 * profile single-slice (SR-WOR-H-17-4) while still ignoring the slice position
 * stored at placement, which on a rotated view was measured along a different
 * normal and would drop the endpoint onto an offset plane. Moves that leave
 * the image are refused. The moved point is stored in the frame of the image
 * the pane shows. The panel re-derives.
 */
export function useEndpointDrag(view: PlaneView | null): {
    beginDrag: (event: React.MouseEvent, drag: EndpointDrag) => void;
    draggingId: string | null;
} {
    const setDraggingId = useSetRecoilState(draggingLineProfileState);

    const anchorPlaneCoordinateMm = useCallback(
        (drag: EndpointDrag, dragView: PlaneView) =>
            viewPlaneCoordinateMm(drag.anchorMm, dragView),
        []
    );

    const movePoint = useRecoilCallback(
        (callback) =>
            (drag: EndpointDrag, pointMm: PointMm, dragView: PlaneView) => {
                const pane = paneImageOf(dragView);
                if (
                    pane.kind === 'none' ||
                    !isPointInsideDisplayedImage(pointMm, dragView)
                ) {
                    return;
                }
                updateAnnotation(
                    callback,
                    lineProfileListState,
                    drag.id,
                    (profile) =>
                        withMovedEndpoint(
                            profile,
                            drag.endpoint,
                            pane.toImageFrame(pointMm)
                        )
                );
            },
        []
    );

    const { beginDrag, dragging } = useAnnotationPointDrag(
        view,
        anchorPlaneCoordinateMm,
        movePoint
    );

    // Mirrored to recoil so the panel can skip re-sampling every co-registered
    // dataset until the drag ends. One overlay is mounted per view host, so
    // several overlays of one series are live at once; only the one that
    // owns the drag writes, and its cleanup alone clears the id, on drag end
    // and on unmount mid-drag. An unconditional write would let any other
    // overlay's remount clear a drag it is not running.
    const draggingId = dragging?.id ?? null;
    useEffect(() => {
        if (!dragging) {
            return;
        }
        setDraggingId(dragging.id);
        return () => setDraggingId(null);
    }, [dragging, setDraggingId]);

    return { beginDrag, draggingId };
}

/**
 * SVG overlay for committed line profiles: draws each line (with a
 * length label and draggable endpoint handles when editable) plus the
 * in-progress rubber-band. Only the handles are pointer-interactive.
 */
export default function LineProfileOverlay({ view }: LineProfileOverlayProps) {
    const profiles = useRecoilValue(lineProfileListState.items);
    const draft = useRecoilValue(lineProfileDraftState);
    const placementActive = useRecoilValue(intensityPlacementActiveState);
    const highlightedId = useRecoilValue(highlightedLineProfileState);
    const { beginDrag, draggingId } = useEndpointDrag(view);

    return (
        <AnnotationPlaneOverlay
            view={view}
            items={profiles}
            draft={draft}
            placementActive={placementActive}
            tool="intensity"
            highlightedId={highlightedId}
            draggingId={draggingId}
            beginDrag={beginDrag}
            itemInFrame={lineProfileInFrame}
            draftInFrame={lineProfileInFrame}
            pointsOf={profilePoints}
            renderItem={renderProfile}
            renderDraft={renderDraft}
        />
    );
}

function renderProfile(
    view: PlaneView,
    profile: LineProfile,
    appearance: AnnotationAppearance,
    beginDrag: (event: React.MouseEvent, drag: EndpointDrag) => void
) {
    const { onCurrentSlice, emphasized, editable } = appearance;
    const start = patientToCanvasPoint(profile.startMm, view);
    const end = patientToCanvasPoint(profile.endMm, view);
    const dimmed = !onCurrentSlice;
    const opacity = emphasized || !dimmed ? 1 : OFF_PLANE_OPACITY;
    const mid = canvasMidpoint(start, end);
    return (
        <g key={profile.id} opacity={opacity}>
            <line
                x1={start.x}
                y1={start.y}
                x2={end.x}
                y2={end.y}
                stroke={PROFILE_COLOR}
                strokeWidth={emphasized ? 2.5 : 1.5}
                strokeDasharray={dimmed ? '4 3' : undefined}
            />
            <text
                x={mid.x}
                y={mid.y - 6}
                textAnchor="middle"
                fontSize={12}
                fill={PROFILE_COLOR}
                style={OVERLAY_LABEL_STYLE}
            >
                {formatDistanceCm(
                    euclideanDistanceMm(profile.startMm, profile.endMm)
                )}
            </text>
            {editable &&
                (['start', 'end'] as const).map((endpoint) => {
                    const at = endpoint === 'start' ? start : end;
                    return (
                        <circle
                            key={endpoint}
                            cx={at.x}
                            cy={at.y}
                            r={HANDLE_PX}
                            fill={PROFILE_COLOR}
                            style={{ pointerEvents: 'all', cursor: 'move' }}
                            onMouseDown={(event) =>
                                beginDrag(event, {
                                    id: profile.id,
                                    endpoint,
                                    anchorMm:
                                        endpoint === 'start'
                                            ? profile.endMm
                                            : profile.startMm,
                                })
                            }
                        />
                    );
                })}
        </g>
    );
}

/** Render the in-progress placement: start dot + rubber-band to the cursor. */
function renderDraft(view: PlaneView, draft: LineProfileDraft) {
    const start = patientToCanvasPoint(draft.startMm, view);
    if (!draft.endMm) {
        return (
            <circle
                key="draft"
                cx={start.x}
                cy={start.y}
                r={HANDLE_PX}
                fill={DRAFT_COLOR}
            />
        );
    }
    const end = patientToCanvasPoint(draft.endMm, view);
    return (
        <g key="draft">
            <line
                x1={start.x}
                y1={start.y}
                x2={end.x}
                y2={end.y}
                stroke={DRAFT_COLOR}
                strokeWidth={1.5}
                strokeDasharray="4 3"
            />
            <circle
                cx={start.x}
                cy={start.y}
                r={HANDLE_PX}
                fill={DRAFT_COLOR}
            />
        </g>
    );
}
